import { useEffect, useRef, useState } from "react";
import { Download, FileText, X } from "lucide-react";
import { API_BASE } from "./api";
import { downloadFile } from "./file-api";
import "./file-attachments.css";

export interface Attachment { id?: string; name: string; file?: File; filePath?: string; }

function AttachmentItem({ attachment, admin }: { attachment: Attachment; admin: boolean }) {
  const { id, name, file } = attachment;
  const filePath = attachment.filePath || `${admin ? "/admin" : ""}/files/${encodeURIComponent(id || "")}`;
  const isImage = /\.(png|jpe?g)$/i.test(name);
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    let active = true;
    let objectUrl = "";
    setUrl(""); setError("");
    if (!isImage) return;
    const controller = new AbortController();
    const load = async () => {
      try {
        let blob: Blob;
        if (file) blob = file;
        else if (id) {
          const response = await fetch(`${API_BASE}${filePath}`, { credentials: "include", signal: controller.signal });
          if (!response.ok) throw new Error("이미지를 불러오지 못했습니다.");
          blob = await response.blob();
        } else return;
        if (active) { objectUrl = URL.createObjectURL(blob); setUrl(objectUrl); }
      } catch (caught) { if (active) setError(caught instanceof Error ? caught.message : "이미지를 불러오지 못했습니다."); }
    };
    void load();
    return () => { active = false; controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [id, file, filePath, isImage]);
  useEffect(() => { if (open) dialog.current?.showModal(); }, [open]);
  const download = async () => {
    if (!id) return;
    try { await downloadFile(filePath, name); }
    catch (caught) { setError(caught instanceof Error ? caught.message : "파일을 다운로드하지 못했습니다."); }
  };
  return <article className="cw-attachment">
    <button type="button" className="cw-attachment-preview" aria-label={`${name} ${isImage ? "미리보기" : "다운로드"}`} disabled={isImage ? !url : !id} onClick={() => isImage ? setOpen(true) : void download()}>
      {url ? <img src={url} alt={`${name} 미리보기`} onError={() => { setUrl(""); setError("이미지를 불러오지 못했습니다."); }} /> : <FileText size={32} aria-hidden="true" />}
    </button>
    <span className="cw-attachment-name">{name}</span>
    {id ? <button type="button" className="cw-attachment-download" onClick={() => void download()}><Download size={14} />다운로드</button> : null}
    {error ? <small className="cw-attachment-error" role="alert">{error}</small> : null}
    {open ? <dialog className="cw-attachment-dialog" ref={dialog} aria-label={`${name} 미리보기`} onClose={() => setOpen(false)}>
      <button type="button" className="cw-attachment-close" aria-label="미리보기 닫기" onClick={() => dialog.current?.close()}><X size={24} /></button>
      <img src={url} alt={name} /><p>{name}</p>
    </dialog> : null}
  </article>;
}

export function FileAttachments({ files, admin = false }: { files: Attachment[]; admin?: boolean }) {
  if (!files.length) return null;
  return <div className="cw-attachments">{files.map((attachment, index) => <AttachmentItem key={attachment.id || `${attachment.name}-${index}`} attachment={attachment} admin={admin} />)}</div>;
}
